"use client";

import * as React from "react";
import { Bell } from "lucide-react";

import { cn } from "@/lib/utils";
import { Button } from "@/shadcnComponents/ui/button";
import { ScrollArea } from "@/shadcnComponents/ui/scroll-area";
import { Tabs, TabsList, TabsTrigger } from "@/shadcnComponents/ui/tabs";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/shadcnComponents/ui/sheet";
import { Badge } from "@/shadcnComponents/ui/badge";
import { Card, CardContent } from "@/shadcnComponents/ui/card";
import { useNotification } from "@/Providers/Notification";
import { useNavigate } from "react-router-dom";
import { formatRelativeTime } from "@/Helpers/times-ago";

export function CreatorNotificationPanel() {
  const notificationState = useNotification();

  const [activeTab, setActiveTab] = React.useState<"all" | "unread" | "read">(
    "all"
  );
  const [isOpen, setIsOpen] = React.useState(false);

  const unreadCount = notificationState?.unreadCount;
  const navigate = useNavigate();

  const filteredNotifications = React.useMemo(() => {
    if (activeTab === "unread") return notificationState?.unreadNotifications;
    if (activeTab === "read") return notificationState?.readNotifications;
    return notificationState?.notifications;
  }, [notificationState, activeTab]);

  const markAsRead = (id: string) => {
    notificationState?.markAsRead(id);
  };

  const markAllAsRead = () => {
    notificationState?.markAllAsRead();
  };

  const isLoadMore = React.useMemo(() => {
    if (activeTab === "unread") {
      return (
        notificationState?.currentUnreadPage < notificationState?.AllUnreadPage
      );
    }

    if (activeTab === "read") {
      return (
        notificationState?.currentReadPage < notificationState?.AllReadPage
      );
    }

    return notificationState?.currentMainPage < notificationState?.AllMainPage;
  }, [activeTab, notificationState]);

  return (
    <Sheet open={isOpen} onOpenChange={setIsOpen}>
      <SheetTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          className="relative text-white hover:bg-transparent hover:text-red-300"
          onClick={() => setIsOpen(true)}
        >
          <Bell className="h-5 w-5" />
          {unreadCount > 0 && (
            <Badge className="absolute -top-1 -right-1 h-5 w-5 p-0 flex items-center justify-center bg-red-500 text-white border-none">
              {unreadCount}
            </Badge>
          )}
        </Button>
      </SheetTrigger>
      <SheetContent
        side="right"
        className="w-[380px] sm:w-[450px] bg-zinc-900 border-zinc-800 text-white p-0"
      >
        <SheetHeader className="p-4 border-b border-zinc-800">
          <div className="flex items-center justify-between">
            <SheetTitle className="text-white">Notifications</SheetTitle>
            {unreadCount > 0 && (
              <Button
                variant="ghost"
                size="sm"
                className="text-zinc-400 hover:text-white hover:bg-zinc-800"
                onClick={markAllAsRead}
              >
                Mark all as read
              </Button>
            )}
          </div>
          <Tabs
            defaultValue="all"
            value={activeTab}
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            onValueChange={(value) => setActiveTab(value as any)}
            className="mt-2"
          >
            <TabsList className="bg-zinc-800 grid w-full grid-cols-3">
              <TabsTrigger
                value="all"
                className="data-[state=active]:bg-zinc-700 data-[state=active]:text-white"
              >
                All
              </TabsTrigger>
              <TabsTrigger
                value="unread"
                className="data-[state=active]:bg-zinc-700 data-[state=active]:text-white"
              >
                Unread
                {unreadCount > 0 && (
                  <Badge className="ml-2 bg-red-500 text-white border-none">
                    {unreadCount}
                  </Badge>
                )}
              </TabsTrigger>
              <TabsTrigger
                value="read"
                className="data-[state=active]:bg-zinc-700 data-[state=active]:text-white"
              >
                Read
              </TabsTrigger>
            </TabsList>
          </Tabs>
        </SheetHeader>

        <ScrollArea className="h-[calc(100vh-120px)] p-4">
          {filteredNotifications?.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-40 text-zinc-500">
              <p>No notifications</p>
            </div>
          ) : (
            <div className="grid gap-3">
              {filteredNotifications?.map((notification) => (
                <Card
                  key={notification?._id}
                  className={cn(
                    "border-zinc-800 bg-zinc-800/30 hover:bg-zinc-800/50 transition-colors cursor-pointer overflow-hidden",
                    !notification?.read && "border-l-4 border-l-red-500"
                  )}
                  onClick={() => {
                    if (!notification?.read) {
                      markAsRead(notification?._id);
                    }
                    if (notification?.redirectionLink) {
                      navigate(notification?.redirectionLink);
                    }
                    if (notification?.externalLink) {
                      window.open(notification?.externalLink, "_blank");
                    }
                  }}
                >
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between mb-2">
                      <Badge className="px-2 py-0.5 text-xs uppercase font-medium text-white border-none bg-zinc-600">
                        {notification?.type}
                      </Badge>
                    </div>

                    <div className="flex items-start gap-2">
                      <div>
                        <h4
                          className={cn(
                            "text-sm text-white",
                            !notification?.read && "font-bold"
                          )}
                        >
                          {notification?.heading}
                        </h4>
                        <p
                          className={cn(
                            "text-sm mt-1",
                            !notification?.read
                              ? "text-white font-medium"
                              : "text-zinc-400"
                          )}
                        >
                          {notification?.subheading}
                        </p>
                        <p className="text-xs text-zinc-500 mt-2">
                          {formatRelativeTime(notification?.createdAt)}
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}

              {isLoadMore && (
                <Button
                  onClick={() =>
                    notificationState?.fetchNextPageNotifications(activeTab)
                  }
                  className="bg-zinc-800/30 hover:bg-zinc-800/50 transition-colors"
                >
                  Load more
                </Button>
              )}
            </div>
          )}
        </ScrollArea>
      </SheetContent>
    </Sheet>
  );
}
